import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { formatMessage, getLanguage } from 'actions/localization';
import queryStringParser from '../utils/queryStringParser';

const showErrorToast = () => {
  const errorTitle = formatMessage('aio.request_error.title');
  const errorMsg = formatMessage('aio.request_error.body');
  toastr.error(errorTitle, errorMsg);
};

const isAioInternalError = (response) => {
  if (response.status !== 200) {
    return false;
  }
  const headers = response.headers || {};
  const size = headers['content-length'];
  const type = headers['content-type'] || null;
  return size === '0' && type === null;
};

const showAioErrorToast = () => {
  const errorTitle = formatMessage('aio.error.title');
  const errorMsg = formatMessage('aio.error.body');
  toastr.error(errorTitle, errorMsg);
};

const isErrorHidden = (response) => {
  const { config = {} } = response;
  if (typeof config.shouldHideErrorForResponse === 'function') {
    return config.shouldHideErrorForResponse(response);
  }
  return config.hideError;
};

const setupAxiosInterceptors = (onUnauthenticated, country) => {
  const countryCode = country || COUNTRY;

  const queryString = location.search;
  const queryParameters = queryStringParser.parse(queryString);
  const onRequestSuccess = (requestConfig) => {
    const config = requestConfig;
    if (config.timeout === 0) {
      config.timeout = 60 * 1000;
    }
    if (COUNTRY === 'PL') {
      config.timeout *= 2;
    }
    let brand = 'credit24';
    if (countryCode === 'MX' || countryCode === 'ES' || countryCode === 'PL') {
      brand = 'hapi';
    } else if (countryCode === 'CZ') {
      brand = 'creditea';
    }
    config.headers['x-brand'] = brand;
    config.headers['x-country'] = countryCode;
    config.headers['x-language'] = getLanguage(countryCode);
    config.headers['Content-type'] = 'application/json';
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers.Pragma = 'no-cache';
    config.headers['x-channel'] = 'loan_app';
    if (MOBILE_WALLET) {
      config.headers['x-channel'] = 'Mobile-Wallet';
    }
    if (SELF_SERVICE) {
      config.headers['X-Origin'] = 'SELF_SERVICE';
    }
    const defaultCreditApplicationChannel = MOBILE_WALLET ? 'MOBILE_WALLET_APP' : 'WEB';

    config.headers['X-Credit-Application-Brand'] = queryParameters.creditApplicationBrand || 'CREDIT24';
    config.headers['X-Credit-Application-Channel'] = queryParameters.creditApplicationChannel || defaultCreditApplicationChannel;
    config.headers['X-Credit-Application-Originator'] = queryParameters.creditApplicationOriginator || 'CUSTOMER';

    return config;
  };
  const onResponseSuccess = (response) => {
    if (isAioInternalError(response)) {
      if (!isErrorHidden(response)) {
        showAioErrorToast();
      }
      return Promise.reject(response);
    }
    return response;
  };
  const onResponseError = (axiosError) => {
    // property 'error.config' is not present on network errors
    const error = axiosError.response || axiosError;
    const config = error.config || {};
    const forbiddenRequest = error.status === 403;
    const errorRemovingSession = error.status === 401 && config.authRequest && error.request.requestMethod === 'DELETE';
    if (forbiddenRequest) {
      showAioErrorToast();
      throw Error('Forbidden');
    }
    if (errorRemovingSession) {
      showAioErrorToast();
      throw Error('Unauthorized');
    }
    const sessionExpired = error.status === 401 && !config.authRequest;
    if (!sessionExpired && !isErrorHidden(error)) {
      showErrorToast();
    }
    if (sessionExpired) {
      onUnauthenticated();
    }
    return Promise.reject(error);
  };
  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
